import {
  NgModule,
  ModuleWithProviders,
  LOCALE_ID,
  ErrorHandler
} from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { DialogEffects } from './dialog/dialog.effects';
import { TableEffects } from './tables/table.effects';
import { NotificationEffects } from './notification/notification.effects';
import { FormsEffects } from './forms/forms.effects';
import { UserEffects } from './authentication/effects/user.effects';
import { AuthEffects } from './authentication/effects/auth.effects';
import { DocumentEffects } from './documents/document.effects';
import { StoreModule } from '@ngrx/store';
import { coreReducers } from './reducer';
import { FeatureFlagsEffects } from './feature-flags/feature-flags.effects';
import { HttpClientModule } from '@angular/common/http';
import { ConfigEffects } from './config/config.effects';
import { RouterStateSerializer } from '@ngrx/router-store';
import { RouterStateSnapshot } from '@angular/router';
import { RouterStateUrl } from './app-state';
import {
  DatePipe,
  CurrencyPipe,
  LocationStrategy,
  PathLocationStrategy,
  Location
} from '@angular/common';
import { ALL_COLLECTION_IDS_TOKEN } from './tokens';
import {
  HAMMER_GESTURE_CONFIG,
  DomSanitizer,
  Title
} from '@angular/platform-browser';
import {
  GestureConfig,
  MatSnackBarModule,
  MatIconRegistry,
  MatIconModule,
  MatNativeDateModule,
  MatDialogModule,
  MatPaginatorIntl
} from '@angular/material';
import { BREAKPOINT } from '@angular/flex-layout';
import { LoadingEffects } from './loading/loading.effects';
import { LifeCycleEffects } from './life-cycle/life-cycle.effects';
import { RootComponentModule } from './root/root.component.module';
import { ScreenEffects } from './layout/effects/screen.effects';
import { WindowEffects } from './windows/window.effects';
import { CustomMatPaginatorIntl } from './custom-mat-paginator-intl';
import { CoreEffects } from './core.effects';
import { StackdriverErrorHandler } from './stackdriver-error-handler';

export interface DigitaixCoreOptions {
  localeId?: string;
  allCollectionIds?: string[];
  bugReportMail?: string;
  providers?: any[];
  production?: boolean;
}

export const CUSTOM_BREAKPOINTS = [
  {
    alias: 'xl',
    suffix: 'xl',
    mediaQuery: 'only screen and (max-width: 1420px)',
    overlapping: false
  },
  {
    alias: 'hdr',
    suffix: 'hdr',
    mediaQuery: 'only screen and (max-width: 1680px)',
    overlapping: false
  },
  {
    alias: 'hd',
    suffix: 'hd',
    mediaQuery: 'only screen and (max-width: 1920px)',
    overlapping: false
  }
];

export class CustomRouterStateSerializer
  implements RouterStateSerializer<RouterStateUrl> {
  public serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    let route = routerState.root;
    let data = { ...route.data };

    while (route.firstChild) {
      route = route.firstChild;
      data = { ...data, ...route.data };
    }

    const {
      url,
      root: { queryParams }
    } = routerState;
    const { params } = route;

    // Only return an object including the URL, params and query params
    // instead of the entire snapshot
    return { url, params, queryParams, data };
  }
}

export const DEFAULT_DIGITAIX_CORE_OPTIONS: DigitaixCoreOptions = {
  localeId: 'de-DE',
  allCollectionIds: [],
  bugReportMail: '',
  providers: [],
  production: false,
};

@NgModule({})
export class DxCoreModule {
  public static forRoot(
    options: DigitaixCoreOptions = {}
  ): ModuleWithProviders {
    return {
      ngModule: DxCoreRootModule,
      providers: [
        DatePipe,
        Location,
        CurrencyPipe,
        { provide: LOCALE_ID, useValue: options.localeId || 'de-DE' },
        {
          provide: ALL_COLLECTION_IDS_TOKEN,
          useValue: options.allCollectionIds || []
        },
        {
          provide: LocationStrategy,
          useClass: PathLocationStrategy
        },
        // TODO : add @digitaix/user-report
        // {
        //   provide: BUG_REPORT_MAIL,
        //   useValue: options.bugReportMail,
        // },
        {
          provide: HAMMER_GESTURE_CONFIG,
          useClass: GestureConfig
        },
        {
          provide: BREAKPOINT,
          useValue: CUSTOM_BREAKPOINTS,
          multi: true
        },
        {
          provide: MatPaginatorIntl,
          useClass: CustomMatPaginatorIntl
        },
        {
          provide: ErrorHandler,
          useClass: options.production ? StackdriverErrorHandler : ErrorHandler,
        }
      ]
    };
  }

  public static forSandbox(): ModuleWithProviders {
    return {
      ngModule: DxCoreSandboxModule,
      providers: [
        DatePipe,
        Location,
        CurrencyPipe,
        { provide: LOCALE_ID, useValue: 'de-DE' },
        {
          provide: ALL_COLLECTION_IDS_TOKEN,
          useValue: []
        },
        {
          provide: LocationStrategy,
          useClass: PathLocationStrategy
        },
        // TODO : add @digitaix/user-report
        // {
        //   provide: BUG_REPORT_MAIL,
        //   useValue: options.bugReportMail,
        // },
        {
          provide: HAMMER_GESTURE_CONFIG,
          useClass: GestureConfig
        },
        {
          provide: BREAKPOINT,
          useValue: CUSTOM_BREAKPOINTS,
          multi: true
        }
      ]
    };
  }
}

@NgModule({
  exports: [HttpClientModule, RootComponentModule, MatNativeDateModule],
  imports: [
    HttpClientModule,
    EffectsModule.forFeature([
      CoreEffects,
      DocumentEffects,
      AuthEffects,
      UserEffects,
      TableEffects,
      DialogEffects,
      FormsEffects,
      LoadingEffects,
      NotificationEffects,
      FeatureFlagsEffects,
      ConfigEffects,
      LifeCycleEffects,
      ScreenEffects,
      WindowEffects
    ]),
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    StoreModule.forFeature('dx', coreReducers)
  ],
  providers: [Title]
})
export class DxCoreRootModule {
  constructor(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    matIconRegistry.addSvgIconSet(
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/mdi.svg')
    );
  }
}

@NgModule({
  exports: [HttpClientModule],
  imports: [
    HttpClientModule,
    EffectsModule.forFeature([
      DocumentEffects,
      TableEffects,
      DialogEffects,
      FormsEffects,
      LoadingEffects,
      NotificationEffects,
      FeatureFlagsEffects,
      ConfigEffects,
      LifeCycleEffects,
      ScreenEffects
    ]),
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    StoreModule.forFeature('dx', coreReducers)
  ],
  providers: [Title]
})
export class DxCoreSandboxModule extends DxCoreModule {}

/**
 * @deprecated use DxCoreModule
 */
export class DigitaixCoreModule {
  public static forRoot = DxCoreModule.forRoot;
}

export function CustomRouterStateSerializerProvider() {
  return {
    provide: RouterStateSerializer,
    useClass: CustomRouterStateSerializer
  };
}

export function CustomHammerGestureConfig() {
  return {
    provide: HAMMER_GESTURE_CONFIG,
    useClass: GestureConfig
  };
}
